import { useState, useEffect, useMemo } from "react";
import { eventsApi } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Plus, Search, Edit2, Trash2, Calendar, Clock, ChevronDown, ChevronUp, History } from "lucide-react";
import { format, parseISO, isAfter, startOfDay } from "date-fns";
import EventDialog from "../components/EventDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../components/ui/collapsible";

// Event color definitions - matching backend
const EVENT_COLORS = {
  blue: { name: "Blue", bg: "bg-blue-100 dark:bg-blue-900/40", border: "border-blue-300 dark:border-blue-700", text: "text-blue-800 dark:text-blue-200", dot: "bg-blue-500" },
  green: { name: "Green", bg: "bg-green-100 dark:bg-green-900/40", border: "border-green-300 dark:border-green-700", text: "text-green-800 dark:text-green-200", dot: "bg-green-500" },
  purple: { name: "Purple", bg: "bg-purple-100 dark:bg-purple-900/40", border: "border-purple-300 dark:border-purple-700", text: "text-purple-800 dark:text-purple-200", dot: "bg-purple-500" },
  red: { name: "Red", bg: "bg-red-100 dark:bg-red-900/40", border: "border-red-300 dark:border-red-700", text: "text-red-800 dark:text-red-200", dot: "bg-red-500" },
  orange: { name: "Orange", bg: "bg-orange-100 dark:bg-orange-900/40", border: "border-orange-300 dark:border-orange-700", text: "text-orange-800 dark:text-orange-200", dot: "bg-orange-500" },
  yellow: { name: "Yellow", bg: "bg-yellow-100 dark:bg-yellow-900/40", border: "border-yellow-300 dark:border-yellow-700", text: "text-yellow-800 dark:text-yellow-200", dot: "bg-yellow-500" },
  teal: { name: "Teal", bg: "bg-teal-100 dark:bg-teal-900/40", border: "border-teal-300 dark:border-teal-700", text: "text-teal-800 dark:text-teal-200", dot: "bg-teal-500" },
  pink: { name: "Pink", bg: "bg-pink-100 dark:bg-pink-900/40", border: "border-pink-300 dark:border-pink-700", text: "text-pink-800 dark:text-pink-200", dot: "bg-pink-500" },
};

const getEventColor = (colorId) => EVENT_COLORS[colorId] || EVENT_COLORS.blue;

// Reusable Events Table component
const EventsTable = ({ events, onEdit, onDelete, emptyMessage, showActions = true }) => {
  const formatEventDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), "EEE, MMM d, yyyy");
    } catch {
      return dateStr;
    }
  };

  const formatEventTime = (startTime, endTime) => {
    return `${startTime} - ${endTime}`;
  };

  if (events.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-24 text-muted-foreground">
        <p>{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Event</TableHead>
            <TableHead>Date</TableHead>
            <TableHead>Time</TableHead>
            <TableHead>Notes</TableHead>
            {showActions && <TableHead className="text-right">Actions</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {events.map((event) => {
            const color = getEventColor(event.color);
            return (
              <TableRow key={event.id} data-testid={`event-row-${event.id}`}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${color.dot}`} />
                    <span className="font-medium">{event.name}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    {formatEventDate(event.date)}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="w-4 h-4 text-muted-foreground" />
                    {formatEventTime(event.start_time, event.end_time)}
                  </div>
                </TableCell>
                <TableCell className="max-w-[200px]">
                  {event.notes ? (
                    <span className="text-sm text-muted-foreground truncate block">
                      {event.notes}
                    </span>
                  ) : (
                    <span className="text-sm text-muted-foreground/50">-</span>
                  )}
                </TableCell>
                {showActions && (
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onEdit(event)}
                        data-testid={`edit-event-${event.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onDelete(event)}
                        className="text-destructive hover:text-destructive"
                        data-testid={`delete-event-${event.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

const Events = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);
  const [pastEventsOpen, setPastEventsOpen] = useState(false);

  // Fetch events
  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await eventsApi.getAll();
      setEvents(response.data);
    } catch (error) {
      console.error("Failed to fetch events:", error);
      toast.error("Failed to load events");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  // Filter events based on search query
  const filteredEvents = useMemo(() => {
    if (!searchQuery.trim()) return events;
    const query = searchQuery.toLowerCase();
    return events.filter(event => 
      event.name.toLowerCase().includes(query) ||
      (event.notes && event.notes.toLowerCase().includes(query))
    );
  }, [events, searchQuery]);

  // Split into upcoming and past events
  const { upcomingEvents, pastEvents } = useMemo(() => {
    const today = startOfDay(new Date());
    
    const upcoming = [];
    const past = [];
    
    filteredEvents.forEach(event => {
      const eventDate = startOfDay(parseISO(event.date));
      if (isAfter(eventDate, today) || eventDate.getTime() === today.getTime()) {
        upcoming.push(event);
      } else {
        past.push(event);
      }
    });
    
    // Sort upcoming by date ascending (soonest first)
    upcoming.sort((a, b) => {
      const dateA = new Date(`${a.date}T${a.start_time}`);
      const dateB = new Date(`${b.date}T${b.start_time}`);
      return dateA - dateB;
    });
    
    // Sort past by date descending (most recent first)
    past.sort((a, b) => {
      const dateA = new Date(`${a.date}T${a.start_time}`);
      const dateB = new Date(`${b.date}T${b.start_time}`);
      return dateB - dateA;
    });
    
    return { upcomingEvents: upcoming, pastEvents: past };
  }, [filteredEvents]);

  const handleAddEvent = () => {
    setEditingEvent(null);
    setDialogOpen(true);
  };

  const handleEditEvent = (event) => {
    setEditingEvent(event);
    setDialogOpen(true);
  };

  const handleDeleteClick = (event) => {
    setEventToDelete(event);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!eventToDelete) return;
    try {
      await eventsApi.delete(eventToDelete.id);
      toast.success("Event deleted successfully");
      fetchEvents();
    } catch (error) {
      console.error("Failed to delete event:", error);
      toast.error("Failed to delete event");
    } finally {
      setDeleteDialogOpen(false);
      setEventToDelete(null);
    }
  };

  const handleSaveEvent = async (eventData) => {
    try {
      if (editingEvent) {
        await eventsApi.update(editingEvent.id, eventData);
        toast.success("Event updated successfully");
      } else {
        await eventsApi.create(eventData);
        toast.success("Event created successfully");
      }
      setDialogOpen(false);
      fetchEvents();
    } catch (error) {
      console.error("Failed to save event:", error);
      toast.error("Failed to save event");
    }
  };

  return (
    <div className="space-y-6" data-testid="events-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Events</h1>
          <p className="text-muted-foreground">
            Manage events like parties, meetings, and special occasions that affect staffing.
          </p>
        </div>
        <Button onClick={handleAddEvent} data-testid="add-event-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Event
        </Button>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search events by name or notes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="events-search-input"
            />
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-center h-32">
              <div className="text-muted-foreground">Loading events...</div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Upcoming Events */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Upcoming Events ({upcomingEvents.length})
              </CardTitle>
              <CardDescription>
                Events scheduled for today and future dates
              </CardDescription>
            </CardHeader>
            <CardContent>
              {upcomingEvents.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
                  <Calendar className="w-8 h-8 mb-2" />
                  <p>{searchQuery ? "No upcoming events match your search" : "No upcoming events"}</p>
                  {!searchQuery && (
                    <Button variant="link" onClick={handleAddEvent} className="mt-2">
                      Create your first event
                    </Button>
                  )}
                </div>
              ) : (
                <EventsTable
                  events={upcomingEvents}
                  onEdit={handleEditEvent}
                  onDelete={handleDeleteClick}
                  emptyMessage="No upcoming events"
                />
              )}
            </CardContent>
          </Card>

          {/* Past Events - Collapsible */}
          {pastEvents.length > 0 && (
            <Collapsible open={pastEventsOpen} onOpenChange={setPastEventsOpen}>
              <Card>
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CardTitle className="flex items-center gap-2">
                          <History className="w-5 h-5" />
                          Past Events ({pastEvents.length})
                        </CardTitle>
                      </div>
                      <Button variant="ghost" size="sm">
                        {pastEventsOpen ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                    <CardDescription>
                      Events that have already occurred
                    </CardDescription>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent>
                    <EventsTable
                      events={pastEvents}
                      onEdit={handleEditEvent}
                      onDelete={handleDeleteClick}
                      emptyMessage="No past events"
                    />
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          )}
        </>
      )}

      {/* Event Dialog */}
      <EventDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        event={editingEvent}
        onSave={handleSaveEvent}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Event</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &ldquo;{eventToDelete?.name}&rdquo;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-event-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="delete-event-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Events;
